/**
 * Injects hreflang + canonical + `<html lang>` tags for the current page
 * (Iter 67dz).
 *
 * Google requires hreflang annotations to be reciprocal: every language
 * version of a page must list every other language version including
 * itself, plus an `x-default` fallback. We emit them as `<link
 * rel="alternate">` elements in `<head>`, swapping them out whenever the
 * URL or locale changes.
 *
 * Why DOM-level injection instead of react-helmet: we need this on every
 * route without per-page boilerplate, and the rest of the app already
 * manages head tags directly (see index.html). Pulling in helmet just
 * for hreflang is overkill.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  CANONICAL_ORIGIN,
  LOCALE_CODES,
  basenameForLocale,
  detectLocaleFromPath,
  localizedAbsoluteUrl,
  stripLocalePrefix,
} from "@/lib/localeRoutes";

const MANAGED_ATTR = "data-locale-seo";

function clearManaged() {
  document.head
    .querySelectorAll(`link[${MANAGED_ATTR}]`)
    .forEach((el) => el.remove());
}

function appendLink(attrs) {
  const el = document.createElement("link");
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
  el.setAttribute(MANAGED_ATTR, "1");
  document.head.appendChild(el);
}

export default function LocaleSeo() {
  const { pathname } = useLocation();

  useEffect(() => {
    // `useLocation` is called *inside* the BrowserRouter whose basename
    // already strips the locale prefix — so pathname here is the
    // "logical" page (e.g. "/cars" even on `/ru/cars`). We still combine
    // it with the URL-detected locale to build alternates for ALL
    // supported languages.
    const currentLocale = detectLocaleFromPath(window.location.pathname);
    const logicalPath = pathname || "/";

    // 1) <html lang="…"> for AT, screen readers, and Google.
    document.documentElement.setAttribute("lang", currentLocale);

    // 2) Wipe previously-managed tags so navigating between pages doesn't
    //    duplicate hreflang links.
    clearManaged();

    // 3) Canonical = the URL of THIS locale version of the page.
    appendLink({
      rel: "canonical",
      href: localizedAbsoluteUrl(currentLocale, logicalPath),
    });

    // 4) Alternates: one per supported locale + x-default → EN.
    for (const code of LOCALE_CODES) {
      appendLink({
        rel: "alternate",
        hreflang: code,
        href: localizedAbsoluteUrl(code, logicalPath),
      });
    }
    appendLink({
      rel: "alternate",
      hreflang: "x-default",
      href: localizedAbsoluteUrl("en", logicalPath),
    });

    // 5) OpenGraph locale follows current language so social previews
    //    look native when shared from a localized URL.
    const ogLocale = document.querySelector('meta[property="og:locale"]');
    if (ogLocale) {
      const map = {
        en: "en_GB", fr: "fr_FR", de: "de_DE", it: "it_IT", es: "es_ES",
        al: "sq_AL", pt: "pt_PT", pl: "pl_PL", ro: "ro_RO", el: "el_GR",
        bg: "bg_BG", tr: "tr_TR", ru: "ru_RU",
      };
      ogLocale.setAttribute("content", map[currentLocale] || "en_GB");
    }

    // Sanity: log a single line in dev so debugging is easy.
    // (Avoids spamming on every navigation in prod — keep behind a guard.)
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.debug(
        `[LocaleSeo] lang=${currentLocale} canonical=${localizedAbsoluteUrl(currentLocale, logicalPath)}`
      );
    }

    return () => {
      // We deliberately DON'T clear on unmount — LocaleSeo is mounted
      // once at app shell level, so unmount only happens on full
      // navigation. Leaving the tags in place avoids a flash where the
      // <head> momentarily has no hreflang during route transitions.
    };
  }, [pathname]);

  return null;
}

// Re-exports kept for convenience in tests / other components.
export { basenameForLocale, CANONICAL_ORIGIN, stripLocalePrefix };
